import isObject from 'lodash/isObject';
import some from 'lodash/some';
import * as XLSX from 'xlsx';

import { triggerToast } from '@components/Base/Notification';
import { TOAST_VARIANT } from '@utils/data/enums';
import { formatDate } from '@utils/dateFormat';

import { AppliedFilters } from '.';
import messages from './messages';
import { Transactions } from './types';

type initialFilters = {
  filters: AppliedFilters;
};

export const getFormattedFilters = (appliedFilters: AppliedFilters) => {
  const { selectedStatus, transactionId, selectedDateRange } = appliedFilters;
  return {
    ...(selectedStatus?.length && {
      status: { _in: selectedStatus.map(({ value }) => value) },
    }),
    ...(transactionId && {
      id: { _in: transactionId },
    }),
    ...(selectedDateRange &&
      selectedDateRange.startDate &&
      selectedDateRange.endDate && {
        created_at: {
          _gte: selectedDateRange.startDate,
          _lte: selectedDateRange.endDate,
        },
      }),
  };
};

export const formatInitialFilters = (searchParams: URLSearchParams) => {
  return (): initialFilters => {
    const queryParams: {
      [key: string]: string | string[] | object | object[];
    } = {};
    for (const [key, value] of searchParams) {
      try {
        queryParams[key] = JSON.parse(decodeURIComponent(value));
      } catch {
        try {
          queryParams[key] = decodeURIComponent(value);
        } catch (e) {
          console.error('Failed to decode URI: ', e);
        }
      }
    }
    return {
      filters: (queryParams.filters as AppliedFilters) || {},
    };
  };
};

export const hasNonEmptyFilters = (filters: AppliedFilters): boolean => {
  return some(filters, (value) => {
    if (isObject(value)) {
      return some(value, Boolean);
    }
    return Boolean(value);
  });
};

export const showToast = (variant: string, title: string, summary: string) => {
  triggerToast({
    variant: variant,
    message: {
      title: title,
      summary: summary,
    },
  });
};

const showDownloadToast = (isSuccess: boolean) => {
  if (isSuccess) {
    showToast(
      TOAST_VARIANT.SUCCESS,
      messages.transaction_download_success_status_header.defaultMessage,
      messages.transaction_download_success_status_summary.defaultMessage,
    );
  } else {
    showToast(
      TOAST_VARIANT.DANGER,
      messages.transaction_download_failed_status_header.defaultMessage,
      messages.transaction_download_failed_status_summary.defaultMessage,
    );
  }
};

const createWorksheetFromData = (data: object[], fileName: string) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');

  XLSX.writeFile(workbook, fileName);
};

const formatTransactionsData = (transaction: Transactions) => {
  return {
    'Transaction ID': transaction.id,
    'Drive Enrollment ID': '-',
    'Candidate Email': transaction.created_by_details?.email || '-',
    'Candidate Name': transaction.created_by_details?.name || '-',
    'Candidate Created On': transaction.created_by_details?.created_at
      ? formatDate(new Date(transaction.created_by_details.created_at), 'withTime')
      : '',
    'Candidate External ID': transaction.created_by_details?.external_id || '-',
    SKU: '-',
    'Transaction Action': '-',
    'Transaction Date': formatDate(new Date(transaction.created_at), 'withTime'),
    Status: transaction.status,
    'SKU Value': '-',
    'Amount Paid': '-',
    'Discount Applied': '-',
    'Voucher Code': '-',
    'Country of Residence': '-',
    'Invoice ID': '-',
  };
};

export const downloadTransactionsExcel = async (data: Transactions[], fileName: string) => {
  try {
    const formattedData = data.map((coupon) => formatTransactionsData(coupon));
    await createWorksheetFromData(formattedData, fileName);
    showDownloadToast(true);
  } catch (error) {
    showDownloadToast(false);
  }
};

export const getFileName = (name: string) => {
  const today = new Date();
  const formattedDate = today.toISOString().split('T')[0];
  return `${name}_${formattedDate}.xlsx`;
};
